import { cn } from '@/lib/utils';
import { useElementWidth } from '@/hooks/useElementWidth';
import {
  allWhole,
  ChartDataTable,
  ChartLegend,
  ChartTooltip,
  labelEvery,
  plotLeftFor,
  showsAxisLabel,
  niceScale,
  PLOT,
  SERIES_TONES,
  useChartCursor,
  type ChartPoint,
  type ChartSeries,
} from './chartParts';

export interface LineChartProps<K extends string> {
  /** What the chart shows — announced, and the caption of its data table. */
  title: string;
  series: ChartSeries<K>[];
  points: ChartPoint<K>[];
  /** Plot height in px. */
  height?: number;
  formatValue?: (value: number) => string;
  /**
   * Thin the x labels from the last point back, so the latest is always named.
   * For timelines that end today; categorical charts leave it off.
   */
  labelFromEnd?: boolean;
  className?: string;
}

/**
 * Lines over time, one per series, on one shared axis.
 *
 * Built from the same parts as `BarChart`. A day with no value (`null`) breaks
 * the line rather than drawing it down to zero — "nobody used it" and "used it
 * for no time" are different answers. A lone day between gaps is a dot.
 *
 * 2px lines with round joins; a crosshair and a ring-wrapped dot per series
 * mark the active day, and the tooltip lists every series there.
 */
export function LineChart<K extends string>({
  title,
  series,
  points,
  height = 200,
  formatValue = (value) => value.toLocaleString('en-IN'),
  labelFromEnd = false,
  className,
}: LineChartProps<K>) {
  const { ref, width } = useElementWidth<HTMLDivElement>();

  const values = points.flatMap((point) => series.map((item) => point.values[item.key] ?? 0));
  const { top, ticks } = niceScale(Math.max(0, ...values), 4, allWhole(values));

  const plotLeft = plotLeftFor(ticks.map(formatValue));
  const plotWidth = Math.max(0, width - plotLeft - PLOT.right);
  const plotHeight = height - PLOT.top - PLOT.bottom;
  const step = points.length > 0 ? plotWidth / points.length : 0;
  const x = (index: number) => plotLeft + index * step + step / 2;
  const y = (value: number) => PLOT.top + plotHeight - (value / top) * plotHeight;
  const every = labelEvery(points.length, plotWidth);

  const { active, handlers } = useChartCursor(points.length, plotLeft, step);

  /** One path per unbroken run, and the lone days a path cannot draw. */
  function segments(key: K) {
    const runs: { index: number; value: number }[][] = [];
    let current: { index: number; value: number }[] = [];
    points.forEach((point, index) => {
      const value = point.values[key];
      if (value === null) {
        if (current.length > 0) runs.push(current);
        current = [];
      } else {
        current.push({ index, value });
      }
    });
    if (current.length > 0) runs.push(current);
    return runs;
  }

  return (
    <figure className={cn('flex flex-col gap-3', className)}>
      <ChartLegend series={series} />

      <div
        ref={ref}
        className="relative rounded-field focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-line"
        tabIndex={0}
        aria-label={`${title}. Use the arrow keys to read each day.`}
        {...handlers}
      >
        {width > 0 && (
          <svg width={width} height={height} aria-hidden className="block overflow-visible">
            {ticks.map((tick) => (
              <g key={tick}>
                <line
                  x1={plotLeft}
                  x2={width - PLOT.right}
                  y1={y(tick)}
                  y2={y(tick)}
                  className="stroke-line"
                  strokeWidth={1}
                />
                <text
                  x={plotLeft - 6}
                  y={y(tick)}
                  textAnchor="end"
                  dominantBaseline="middle"
                  className="fill-ink-3 text-2xs tabular-nums"
                >
                  {formatValue(tick)}
                </text>
              </g>
            ))}

            {points.map((point, index) =>
              showsAxisLabel(index, points.length, every, labelFromEnd) ? (
                <text
                  key={point.label}
                  x={x(index)}
                  y={height - 6}
                  textAnchor="middle"
                  className="fill-ink-3 text-2xs"
                >
                  {point.axisLabel ?? point.label}
                </text>
              ) : null,
            )}

            {active !== null && (
              <line
                x1={x(active)}
                x2={x(active)}
                y1={PLOT.top}
                y2={PLOT.top + plotHeight}
                className="stroke-line-strong"
                strokeWidth={1}
              />
            )}

            {series.map((item) =>
              segments(item.key).map((run) =>
                run.length === 1 ? (
                  <circle
                    key={`${item.key}-${run[0].index}`}
                    cx={x(run[0].index)}
                    cy={y(run[0].value)}
                    r={3}
                    className={SERIES_TONES[item.tone].fill}
                  />
                ) : (
                  <polyline
                    key={`${item.key}-${run[0].index}`}
                    points={run.map((p) => `${x(p.index)},${y(p.value)}`).join(' ')}
                    fill="none"
                    strokeWidth={2}
                    strokeLinejoin="round"
                    strokeLinecap="round"
                    className={SERIES_TONES[item.tone].stroke}
                  />
                ),
              ),
            )}

            {active !== null &&
              series.map((item) => {
                const value = points[active]?.values[item.key];
                if (value === null || value === undefined) return null;
                return (
                  <circle
                    key={item.key}
                    cx={x(active)}
                    cy={y(value)}
                    r={4}
                    strokeWidth={2}
                    className={cn('stroke-surface', SERIES_TONES[item.tone].fill)}
                  />
                );
              })}
          </svg>
        )}

        {active !== null && points[active] && (
          <ChartTooltip
            point={points[active]}
            series={series}
            formatValue={formatValue}
            x={x(active)}
            width={width}
          />
        )}
      </div>

      <ChartDataTable caption={title} points={points} series={series} formatValue={formatValue} />
    </figure>
  );
}
